import { gregorianCalendar } from "./_calendars";
import { DateTimeTokens } from "./datetime-tokens";
import bigInt from "big-integer";

//The size of the UI "Cell" displayed on the Timelines (px)
const DEFAULT_STEP_SIZE = 15;

//Default Config
const defaultOptions = {
  calendar: gregorianCalendar,
  logSteps: false,
};

//Default Limits
const defaultLimitOptions = {
  includeLowerLimit: true,
  includeUpperLimit: true,
  log: false,
};

//Default Segment Config
const defaultStepOptions = {
  steps: 1,
  stepSize: DEFAULT_STEP_SIZE || 15,
};

const defaultSegmentOptions = {
  unitCountDisplay: 1000,
  // // maxIterations: 10,
};

const defaultUnitDifferenceOptions = {
  granularities: ["aeon", "epoch", "myriad", "millenium", "century", "decade"],
};

/**
 * @name: CHRONOS DATE LIBRARY
 * @description This library creates an instance of a date for Chronos. The
 * three main formats it can return are a String, a RelativeDate (as a person
 * would reckon the date/time), and AbsoluteDuration (also known as a "Full
 * Object" - contains the number of each time unit elapsed from Zero)
 */
export class ChronosDate {
  constructor(input, options) {
    // console.log('Input', input);
    if (!input || typeof input !== "string") {
      throw "Unable to create Chronos Date: Missing or non-string input value.";
    }
    this.input = input;

    let startOptions = { ...defaultOptions };
    if (options) {
      Object.assign(startOptions, options);
    }
    this.initOptions = options ? options : {};
    this.options = startOptions;
    this.calendar = startOptions.calendar;
  }

  /**
   * @description This function validates an input Date to ensure it matches
   * the expected format(s) supported by ChronosDate.
   */
  static validateDateInput(date) {
    if (!date || date === "") {
      return false;
    }
    if (typeof date === "string") {
      const validRegex = /^-?\d+$/i;
      if (validRegex.test(date)) {
        return true;
      }
      return false;
    }
    if (date instanceof ChronosDate) {
      return true;
    }
    return false;
  }

  /**
   * @description This function checks if the instance date is between two
   * other dates. These can be passed as a String.
   */
  isBetween(beginningDate, endingDate, options = {}) {
    //Initialize options
    const initOptions = { ...defaultLimitOptions, ...options };
    //Validate the dates as Strings
    if (!beginningDate || typeof beginningDate !== "string") {
      if (!endingDate || typeof endingDate !== "string") {
        return console.error(
          "Unable to parse dates. Must include both a beginning date and an ending date.",
          { beginningDate, endingDate, parserOptions: initOptions }
        );
      } else {
        return console.error(
          "Unable to parse dates. Must include a beginning date.",
          { beginningDate, endingDate, parserOptions: initOptions }
        );
      }
    }

    if (!endingDate || typeof endingDate !== "string") {
      return console.error(
        "Unable to parse dates. Must include an ending date.",
        { beginningDate, endingDate, parserOptions: initOptions }
      );
    }

    //Configure the operation
    if (initOptions.log) {
      console.log("Operation Options:", beginningDate, endingDate, initOptions);
    }

    let greaterThan = "greater";
    let lesserThan = "lesser";

    if (initOptions.includeLowerLimit) {
      lesserThan = "lesserOrEquals";
    }
    if (initOptions.includeUpperLimit) {
      greaterThan = "greaterOrEquals";
    }

    const dateA = bigInt(beginningDate);
    const dateB = bigInt(endingDate);

    //Compare the dates
    return (
      bigInt(this.input)[greaterThan](dateA) &&
      bigInt(this.input)[lesserThan](dateB)
    );
  }

  /**
   *
   * @param {String} date
   * @param {Object} options
   * @returns {Boolean}
   * @description We check to see if a given date occurs after the instance date
   */
  isAfter(date, options = {}) {
    //Initialize the operation options
    const initOptions = {
      includeLimit: options?.includeLimit || false,
    };

    //Check to ensure the passed date is a String
    if (!date || typeof date !== "string") {
      console.error(
        "Unable to compare dates. Supplied date is missing or not a string.",
        { compareDate: date, parserOptions: initOptions }
      );
      return false;
    }

    //Configure the operation
    let operation = "greater";
    if (initOptions.includeLimit) {
      operation = "greaterOrEquals";
    }

    //Compare and return the result
    return bigInt(this.input)[operation](date);
  }

  /**
   *
   * @param {Sting} date
   * @param {Object} options
   * @returns {Boolean}
   * @description Checks if a given date occurs before the instance date
   */
  isBefore(date, options = {}) {
    //Parse the passed options
    const initOptions = {
      includeLimit: options?.includeLimit || false,
    };

    //Validate that a date was given
    if (!date || typeof date !== "string") {
      console.error(
        "Unable to compare dates. Supplied date is missing or not a string.",
        { compareDate: date, parserOptions: initOptions }
      );
      return false;
    }

    //Configure Operation
    let operation = "lesser";
    if (initOptions.includeLimit) {
      operation = "lesserOrEquals";
    }

    //Perform comparison and return the result
    return bigInt(this.input)[operation](date);
  }

  /**
   * @param {Boolean} asNumber Whether to return the result as a number
   * @returns {String || Number}
   * @description Get the absolute value of the instance date as a string or number
   */
  abs(asNumber) {
    //If the calling operation requires an integer
    if (asNumber) {
      //Return the absolute value as a number
      return bigInt(this.input).abs().toJSNumber();
    }
    //Instead, return the absolute value as a string
    return bigInt(this.input).abs().toString();
  }

  /**
   *
   * @param {Object || String} date Optional. Determine if either a given or the instance date is in negative space
   * @returns {Boolean}
   */
  isNegative(date) {
    //If no date was passed, use the instance date
    if (!date || date === undefined) date = this.input;

    //If the date was a string, simply check if it's less than 0
    if (typeof date === "string") {
      return bigInt(this.input).lesser("0");
    } else {
      //If the date is not a string, we need to check if it's an object
      if (Object.prototype.toString.call(date) === "[object Object]") {
        //We need to loop through the keys of the date object
        const keys = Object.keys(date);
        let isNegative = false;

        //If ANY key's value is less than 0, the date is negative.
        for (let k = 0; k < keys.length; k++) {
          if (date[keys[k]] < 0) {
            isNegative = true;
            break;
          }
        }

        //Return the result
        return isNegative;
      }
    }
  }

  /**
   *
   * @param {String} granularityUnit The unit granularity for the measurement
   * @param {Object} options Optional. Configuration for the operation
   * @returns {Number} Number of the specified unit
   */
  getUnits(granularityUnit, options = { absolute: true }) {
    //Ensure that a unit granularity was passed
    if (!granularityUnit || typeof granularityUnit !== "string") {
      console.warn("Missing or invalid granularity unit. Returning Seconds.");
      return this.input;
    }

    //Configure the operation
    const parserOptions = {
      absolute: true,
      ...options,
    };

    //Units that require special logic
    const advancedUnits = [
      "aeon",
      "epoch",
      "myriad",
      "millenium",
      "century",
      "decade",
      "year",
    ];

    //Check if the given granularity unit is in the preceeding list
    if (advancedUnits.indexOf(granularityUnit) > -1) {
      // console.log('Advanced unit logic');
      //Get the Cycle (Ordered list of all year lengths for each standard and leap year)
      const cycle = this.getCycle();
      //Get the total of all days in a Cycle
      const cycleLengthDays = cycle.reduce((t, yearLengthDays) => {
        return t + yearLengthDays;
      }, 0);
      //Get the number of days that occurred
      const daysTotal = bigInt(this.abs()).divide(
        this.calendar.time.day.duration
      );

      //Determine how many full Cycles have occurred
      let cycleCount = bigInt(daysTotal).divide(cycleLengthDays);

      //Get the initial year count
      let year = bigInt(cycleCount).times(cycle.length);

      //Determine how many days remain
      let remainingDays = bigInt(daysTotal).mod(cycleLengthDays);

      //We need to parse the remaining days to get a more precise year
      for (let y = 0; y < cycle.length; y++) {
        //If the number of days remaining are equal or larger than the number of days in a cycle's year
        if (bigInt(remainingDays).greaterOrEquals(cycle[y])) {
          //We need to remove the number of days from those that remain, and increment the year
          remainingDays = remainingDays.minus(cycle[y]);
          year = year.plus(1);
          //We don't care about the remaining days if they're less than a year.
        } else break;
      }

      //Negative dates have an offset of 1 (since you can't have negative 0)
      if (this.isNegative()) {
        year = year.plus(1);
      }

      //Determine how many years are in the given granularity unit
      const yearsInUnit = bigInt(this.calendar.time[granularityUnit].duration)
        .divide(this.calendar.time.year.duration)
        .toJSNumber();

      //Divide the year by the number of years in the granularity unit to get the total number of units
      const units = bigInt(year).divide(yearsInUnit).toJSNumber();
      return units;
    } else {
      // console.log('Basic unit logic');
      //Basic operation. Just determine how many of the given unit occur.
      const unitDuration = this.calendar.time[granularityUnit].duration;
      return bigInt(this.input).divide(unitDuration).toJSNumber();
    }
  }

  /**
   * @deprecated
   * @param {Boolean} absolute
   * @returns {String}
   * @description ???
   */
  // getSecond(absolute = false) {
  //   let absoluteDate = bigInt(this.input).abs().toString();
  //   let isNegative = bigInt(this.input).lesser('0');
  //   for (let unit of defaultUnits) {
  //     const unitDuration = this.calendar.time[unit]?.duration || undefined;
  //     if (unitDuration) {
  //       if (bigInt(unitDuration).lesserOrEquals(absoluteDate)) {
  //         const unitCount = bigInt(absoluteDate).divide(unitDuration);
  //         if (unitCount.greater('0')) {
  //           const unitCountDuration = unitCount.times(unitDuration);
  //           absoluteDate = bigInt(absoluteDate)
  //             .minus(unitCountDuration)
  //             .toString();
  //         }
  //       }
  //     }
  //   }
  //   if (!absolute && isNegative) {
  //     return bigInt(absoluteDate).times('-1').toJSNumber();
  //   }
  //   return bigInt(absoluteDate).toJSNumber();
  // }

  // getMinute(absolute = false) {
  //   if (absolute) {
  //     return this._getUnit('minute');
  //   }
  //   return this._getUnit('minute', { allowNegative: true });
  // }

  // getHour(absolute = false) {
  //   if (absolute) {
  //     return this._getUnit('hour');
  //   }
  //   return this._getUnit('hour', { allowNegative: true });
  // }

  // getDay(absolute = false) {
  //   if (absolute) {
  //     return this._getUnit('day');
  //   }
  //   return this._getUnit('day', { allowNegative: true });
  // }

  // getYear(absolute = false) {
  //   if (absolute) {
  //     return this._getUnit('year');
  //   }
  //   return this._getUnit('year', { allowNegative: true });
  // }

  // _getUnit(timeUnit, options = { allowNegative: false }) {
  //   let absoluteDate = bigInt(this.input).abs().toString();
  //   let isNegative = bigInt(this.input).lesser('0');
  //   for (let unit of defaultUnits) {
  //     // console.log('unit', unit);
  //     const unitDuration = this.calendar.time[unit]?.duration || undefined;
  //     // console.log('unit duration', unitDuration);
  //     if (unitDuration) {
  //       if (bigInt(unitDuration).lesserOrEquals(absoluteDate)) {
  //         // console.log('here');
  //         const unitCount = bigInt(absoluteDate).divide(unitDuration);
  //         if (unitCount.greater('0')) {
  //           const unitCountDuration = unitCount.times(unitDuration);
  //           absoluteDate = bigInt(absoluteDate)
  //             .minus(unitCountDuration)
  //             .toString();
  //         }
  //         if (unit === timeUnit) {
  //           if (options.allowNegative && isNegative) {
  //             return bigInt(unitCount).times('-1').toJSNumber();
  //           }
  //           return bigInt(unitCount).toJSNumber();
  //         }
  //       } else {
  //         if (unit === timeUnit) {
  //           return 0;
  //         }
  //       }
  //     }
  //   }
  // }

  // asObject() {
  //   const newDate = {
  //     year: this.getYear(),
  //     day: this.getDay(),
  //     hour: this.getHour(),
  //     minute: this.getMinute(),
  //     second: this.getSecond(),
  //   };
  //   return newDate;
  // }

  /**
   *
   * @returns {String}
   * @description Returns the instance date as a date string
   */
  asString() {
    return this.input;
  }

  /**
   *
   * @param {String} dateString A given date string
   * @description Sets the instance date string to that given
   * @deprecated
   */
  setTo(dateString) {
    if (isValidDate(dateString)) {
      if (typeof dateString === "string") {
        this.input = dateString;
      }
    } else {
      console.error(
        "Unable to update this ChronosDate. Invalid format, expected numeric string or another ChronosDate object."
      );
    }
  }

  /**
   *
   * @param {String} granularity The granularity needed
   * @param {Object} options Optional. Options for the operation
   * @returns {Number} The number of the given granularity units
   */
  getPositionByGranularity(granularity, options = { ...defaultStepOptions }) {
    const units = this.getUnits(granularity);
    const steps = options?.steps || 1;
    const stepSize = options?.stepSize || DEFAULT_STEP_SIZE;
    const position = bigInt(units)
      .times(steps * stepSize)
      .toJSNumber();
    return position;
  }

  log(...args) {
    if (this.options.logSteps) {
      console.log(...args);
    }
  }

  /**
   * Relative position from start of segment from Granularity
   */
  getPositionRelativeToStart(laneStart, granularity = "year") {
    /***
     * Test cases :
     * - this.input === lanestart => return 0px
     * - this.input === lanestart + 15*granularity.length => return 15*15px
     * - this.input === lanestart + 985*granularity.length => return 985*15px
     * - this.input === lanestart + 1000*granularity.length => 1000*15px === container.width
     */
    const { time } = this.calendar;
    const advancedUnits = [
      "aeon",
      "epoch",
      "myriad",
      "millenium",
      "century",
      "decade",
      "year",
    ];

    if (advancedUnits.indexOf(granularity) > -1) {
      //Years+
      /**
       * let diff=this.input - lanestart
       * daysDiff= diff/days.duration
       * yearsOfDiff=ChronosDate(lanestart).getLengthInYears(daysDiff)
       * return (yearsOfDiff/yearsInUnit)*15
       */

      const relativeDate = this.toRelativeDate();
      const laneStartRelativeDate = this.toRelativeDate(
        this.toFullObject(laneStart)
      );
      const yearsDiff = relativeDate.year - laneStartRelativeDate.year;
      // const yearsDiff = bigInt(this.input).minus(laneStart);
      // const yearsMod = bigInt
      const yearsInUnit = bigInt(time[granularity].duration)
        .divide(time.year.duration)
        .toJSNumber();
      // return bigInt(yearsDiff).divide(yearsInUnit).times(15).toJSNumber();
      return Math.round(yearsDiff / yearsInUnit) * 15;
      // const diff = bigInt(this.input).minus(laneStart);
      // const cycle = this.getCycle();
      // if (bigInt(this.input).lt(0)) {
      //   cycle.reverse();
      // }
      // const daysCount = bigInt(diff).divide(time.day.duration);
      // const cycleLengthDays = cycle.reduce((t, yearLength) => {
      //   return t + yearLength;
      // }, 0);
      // let cycleCount = daysCount.divide(cycleLengthDays);
      // let remainingDays = daysCount.mod(cycleLengthDays);
      // let year = cycleCount.times(cycle.length);
      // for (let y = 0; y < cycle.length; y++) {
      //   if (remainingDays.gte(cycle[y])) {
      //     remainingDays = remainingDays.minus(cycle[y]);
      //     year++;
      //   } else break;
      // }
      // const yearsInUnit = bigInt(time[granularity].duration).divide(
      //   time.year.duration
      // );
      // return bigInt(year).divide(yearsInUnit).times(15);
    } else {
      //Days, Hours, Minutes
      const unitDuration = time[granularity].duration;
      // return ((input - lanestart)/UnitDuration)*15
      return bigInt(this.input)
        .minus(laneStart)
        .divide(unitDuration)
        .times(15)
        .toJSNumber();
    }
  }

  getPositionFromStart(laneStart, granularity = "year") {
    this.log("Date:", this.input);
    this.log("Lane Start:", laneStart);
    this.log("Granularity:", granularity);
    let diff = bigInt(this.abs()).minus(bigInt(laneStart).abs()); //.abs()
    this.log("Difference", diff.toString());
    const { time } = this.calendar;
    const advancedUnits = [
      "aeon",
      "epoch",
      "myriad",
      "millenium",
      "century",
      "decade",
      "year",
    ];
    if (advancedUnits.indexOf(granularity) > -1) {
      const yearsInUnit = bigInt(time[granularity].duration)
        .divide(time.year.duration)
        .toJSNumber();
      this.log("POS Diff:", diff);
      // let diff = bigInt(this.abs()).minus(bigInt(laneStart).abs()).abs();
      let units =
        Math.abs(this.toFullObject(diff.toString()).year) / yearsInUnit;
      const dateObject = this.toFullObject(diff.toString());
      this.log("FULL Obj For DIFF", { ...dateObject });
      // const units = bigInt(dateObject.year).divide(yearsInUnit).toJSNumber();
      return units * 15;
    } else {
      // TODO: Might need further debugging
      const unitDuration = this.calendar.time[granularity].duration;
      const units = bigInt(diff).divide(unitDuration).toJSNumber();
      return units * 15;
    }
  }

  isLeapYear() {
    const { months } = this.calendar;
    const yearDuration = this.calendar.time.year.duration;
    const year = bigInt(this.input).divide(yearDuration);
    let isLeap = false;
    for (let m = 0; m < months.length; m++) {
      const month = months[m];
      if (month.leapPeriod > 0 && month.leapDays > 0) {
        if (year.mod(month.leapPeriod).eq(0)) {
          isLeap = true;
          //Stop checking - we know the result is true.
          break;
        }
      }
    }
    return isLeap;
  }

  getLeapDays() {
    const { months } = this.calendar;
    const yearDuration = this.calendar.time.year.duration;
    const year = bigInt(this.input).divide(yearDuration);
    let leapDays = 0;
    for (let m = 0; m < months.length; m++) {
      const month = months[m];
      if (month.leapPeriod > 0 && month.leapDays > 0) {
        if (year.mod(month.leapPeriod).eq(0)) {
          leapDays += month.leapDays;
        }
      }
    }
    return leapDays;
  }

  getLeapMonths(calendar) {
    if (!calendar || calendar === undefined) {
      calendar = this.calendar;
    }

    const { months } = calendar;
    return months.filter((m) => m.leapPeriod > 0 && m.leapDays > 0);
  }

  getCycle(calendar) {
    if (!calendar) {
      calendar = this.calendar;
    }
    const { time } = calendar;
    const leapMonths = this.getLeapMonths();
    const cycleLength = leapMonths.reduce((t, month) => {
      return t * month.leapPeriod;
    }, 1);

    let cycle = Array(cycleLength).fill(0);

    for (let y = 0; y < cycle.length; y++) {
      let daysInYear = bigInt(time.year.duration).divide(time.day.duration);
      for (let m = 0; m < leapMonths.length; m++) {
        if (y % leapMonths[m].leapPeriod === 0) {
          daysInYear = daysInYear.plus(leapMonths[m].leapDays);
        }
      }
      cycle[y] = daysInYear.toJSNumber();
    }

    return cycle;
  }

  getTimelineSegment(granularity, options = { ...defaultSegmentOptions }) {
    const parserOptions = {
      ...defaultSegmentOptions,
      ...options,
    };
    const { unitCountDisplay: unitCount } = parserOptions;
    const mult = this.isNegative(this.input) ? -1 : 1;

    if (!this.calendar.time[granularity]) {
      return console.error(
        `Unable to locate granularity option "${granularity}"`
      );
    }
    const { time } = this.calendar;
    const unitDuration = bigInt(time[granularity].duration);
    // const unitDuration = 10;
    this.log(
      "Segment on Date",
      // this.asString(),
      this.input,
      // this.toFullObject(),
      granularity
    );
    // let begin = bigInt(this.abs());
    const advancedUnits = [
      "aeon",
      "epoch",
      "myriad",
      "millenium",
      "century",
      "decade",
      "year",
    ];
    if (advancedUnits.indexOf(granularity) > -1) {
      // console.group('=== Segment Calculating');
      const yearsInUnit = bigInt(unitDuration).divide(time.year.duration);
      // console.log("Years in unit", yearsInUnit.toJSNumber());
      const cycle = this.getCycle();
      const cycleDays = cycle.reduce((t, yearLength) => {
        return t + yearLength;
      }, 0);
      // console.log("Days in Leap Cycle", cycleDays);
      let days = bigInt(this.abs())
        // .times(yearsInUnit)
        .divide(time.day.duration);
      // console.log("Days total", days);
      const cycleIterations = days.divide(cycleDays).toJSNumber();
      let years = cycle.length * cycleIterations;
      let remainingDays = days.mod(cycleDays).toJSNumber();

      if (this.isNegative()) {
        // console.log("Reversing cycle!");
        cycle.reverse();
        // years += 1;
        // years += mult < 0 ? 1 : 0; //added to ensure going back from zero works correctly
      }

      // console.log("Years Before reducing: ", years);
      for (let y = 0; y < cycle.length; y++) {
        if (remainingDays >= cycle[y]) {
          remainingDays = bigInt(remainingDays).minus(cycle[y]);
          years += 1;
        } else break;
      }
      // console.log("Seg, years", years);
      // console.log("Unit Count:", unitCount, years);
      let yearsOffset = bigInt(years)
        // .mod(unitCount)

        // .times(yearsInUnit)
        .divide(unitCount * yearsInUnit.toJSNumber())
        .times(mult);
      // .divide(unitCount * (unitCount / yearsInUnit.toJSNumber())); //Given 2050 CE, we're at 2
      if (mult === 1) {
        // const yearsMod = bigInt(years).mod(unitCount);
      } else {
        // const yearsMod = bigInt(years).mod(unitCount).times(mult);
        // console.log({
        //   yearsOffset: yearsOffset,
        //   years: years,
        // });
        const yearsInSegment = yearsOffset
          .abs()
          .times(unitCount * yearsInUnit.toJSNumber());
        const yearsMod = bigInt(years)
          .mod(yearsInSegment.neq(0) ? yearsInSegment : 1)
          .times(mult);
        // console.log("factoring yearsmod :", yearsMod);
        if (!bigInt(yearsMod).eq(0)) {
          this.log(
            "Years Mod",
            yearsMod,
            yearsOffset.toJSNumber(),
            " Subtracting 1..."
          );
          yearsOffset = yearsOffset.minus(1);
          // yearsOffset = bigInt(years - 1).ti
        }
      }

      this.log("Years OFFSET", yearsOffset);
      const yearsStart = bigInt(yearsOffset).times(
        unitCount * yearsInUnit.toJSNumber()
      ); // -2000
      this.log("Years Start", yearsStart);
      const yearsLeapIterations = bigInt(yearsStart).abs().divide(cycle.length); //500
      let yearsStartDays = bigInt(yearsLeapIterations).times(cycleDays);
      let remainingYearsStart = bigInt(yearsStart)
        .abs()
        .mod(cycle.length)
        .toJSNumber();
      // console.log("RemainingYears (after MOD): ", remainingYearsStart);
      if (remainingYearsStart > 0) {
        let daysToAdd = cycle
          .slice(0, remainingYearsStart)
          .reduce((t, yearLength) => t + yearLength, 0);
        yearsStartDays = bigInt(yearsStartDays).plus(daysToAdd);
      }
      // console.log("YearsStartDays: ", yearsStartDays);
      // const leapDays = bigInt(yearsLeapIterations).times(daysInLeapCycle);
      const daysStart = bigInt(yearsStartDays)
        .times(time.day.duration)
        .times(mult);
      // console.log("DaysStart: ", daysStart);
      //Next, span of time
      const yearsEnd = bigInt(yearsStart).plus(
        unitCount * yearsInUnit.toJSNumber()
      ); //multiply unitcount by yearsInUnit
      // const yearsEnd =
      //   mult < 0
      //     ? bigInt(yearsStart).minus(unitCount)
      //     : bigInt(yearsStart).plus(unitCount);
      this.log("YEARS END", yearsEnd);
      const yearsEndLeapCycles = bigInt(yearsEnd).abs().divide(cycle.length);
      let yearsEndDays = bigInt(yearsEndLeapCycles).times(cycleDays);
      let remainingYears = bigInt(yearsEnd)
        .abs()
        .mod(cycle.length)
        .toJSNumber();
      if (remainingYears > 0) {
        let daysToAdd = cycle
          .slice(0, remainingYears)
          .reduce((t, yearLength) => t + yearLength, 0);
        yearsEndDays = bigInt(yearsEndDays).plus(daysToAdd);
      }
      const daysEnd = bigInt(yearsEndDays).times(time.day.duration).times(mult);
      this.log("DaysEnd ", daysEnd, yearsEndDays, mult);
      const segment = {
        units: unitCount,
        offsetUnits: 0,
        offset: 0,
        start: bigInt.min(daysStart, daysEnd).toString(),
        end: bigInt.max(daysStart, daysEnd).toString(),
        // start: daysStart.toString(),
        // end: daysEnd.toString(),
        // start: mult < 0 ? daysEnd.toString() : daysStart.toString(),
        // end: mult < 0 ? daysStart.toString() : daysEnd.toString(),
        // start: daysStart.gt(daysEnd)
        //   ? daysEnd.toString()
        //   : daysStart.toString(),
        // end: daysEnd.lt(daysStart) ? daysStart.toString() : daysEnd.toString(),
      };
      this.log("SEGMENT (in lib)", segment);
      // console.groupEnd();
      return segment;
    }

    // console.group('== Segment ==');
    let offset;
    if (mult < 0) {
      offset = bigInt(this.abs())
        .minus(1)
        .divide(bigInt(unitDuration).times(unitCount));
    } else {
      offset = bigInt(this.abs()).divide(bigInt(unitDuration).times(unitCount));
    }

    if (mult < 0) {
      offset = offset.plus(1);
    }
    this.log("OFFSET (adjusted)", offset);
    let startValue = bigInt(offset)
      .times(unitDuration)
      .times(unitCount)
      .times(mult);

    this.log("Unit Count", unitCount, "UnitDuration", unitDuration);

    let spanValue = bigInt(unitCount).times(unitDuration);
    let endValue = startValue.plus(spanValue);

    // console.log(
    //   'Adjusted Units',
    //   offset,
    //   startValue,
    //   endValue,
    //   this.asString()
    // );

    // console.groupEnd();
    return {
      units: unitCount,
      start: startValue.toString(),
      end: endValue.toString(),
      span: spanValue.toString(),
    };
  }

  getUnitDifference(
    compareDate,
    granularity = "year",
    options = { ...defaultUnitDifferenceOptions }
  ) {
    const dateB = new ChronosDate(compareDate, { calendar: this.calendar });

    return bigInt(this.getUnits(granularity))
      .minus(dateB.getUnits(granularity))
      .toJSNumber();
  }

  toFullObject(startDate) {
    // console.group("-- TO Full Object");
    this.log("Date String", startDate ? startDate : this.input);
    let dateString = startDate
      ? bigInt(startDate).abs().toString()
      : this.abs().slice();

    const isNegative = startDate ? bigInt(startDate).lt(0) : this.isNegative();
    const { time, months } = this.calendar;

    let dateCumulative = {
      year: "",
      day: "",
      hour: "",
      minute: "",
    };

    const dateUnits = Object.keys(dateCumulative);

    //Get all months that have leap days configured
    let leapMonths = months.filter((m) => m.leapPeriod > 0 && m.leapDays > 0);

    //Get the total number of leap periods for the months
    let totalLeapFreq = leapMonths.reduce((t, month) => {
      return t * month.leapPeriod;
    }, 1);
    this.log("Total Frequency", totalLeapFreq); //4

    //Create a cycle of years, to account for differing leap frequencies;
    let yearsArray = Array(totalLeapFreq).fill(0);
    //Loop through each instance of a leapyear cycle
    for (let y = 0; y < yearsArray.length; y++) {
      //Get the number of days in a non-leap year
      let days = bigInt(time.year.duration)
        .divide(time.day.duration)
        .toJSNumber();

      //Cycle throough all the months in a year
      for (let m = 0; m < months.length; m++) {
        // Check if this cycle year is a leap year for the current month
        if (y % months[m].leapPeriod === 0) {
          //This year is a leap year, so we need to add the number of leap days
          days += months[m].leapDays;
        }
      }
      //Set the cycle year's number of days.
      yearsArray[y] = days;
    }

    if (isNegative) {
      yearsArray.reverse();
    }

    this.log("Leap Cycle (years in days)", yearsArray);

    //Add up all the cycle year days
    const sumOfAllLeapDays = yearsArray.reduce((t, year) => {
      return t + year;
    }, 0);
    // console.log("Sum of all Leap Days in Cycle", sumOfAllLeapDays);
    //Iterate through each time unit
    for (let u = 0; u < dateUnits.length; u++) {
      //Get the time unit
      const unit = dateUnits[u];
      const { duration: unitDuration } = time[unit];
      //Calculate the number of time units
      const unitCount = bigInt(dateString).divide(unitDuration).toJSNumber();

      //We're gonna skip days because years has special handling that accounts for them
      if (unit === "day") continue;

      //Years have special handling that also calculates days (due to leap years)
      if (unit === "year") {
        //Get the number of days in the initial date string
        const days = bigInt(dateString).divide(time.day.duration);
        this.log("Days in Datestring", days.toJSNumber());
        //Reduce the date string by the number of days (in seconds)
        dateString = bigInt(dateString)
          .minus(bigInt(days).times(time.day.duration))
          .toString();
        this.log("Remaining Time to factor: ", dateString);
        //Calculate how many full leap cycles occur within the date
        const fullCycles = days.divide(sumOfAllLeapDays);
        let years = fullCycles * yearsArray.length;
        // console.log("years!", years);
        //Get the remaining days left over after all leap cycle (years)
        let remainder = days.mod(sumOfAllLeapDays);
        this.log("Number of Full Cycles", fullCycles);
        this.log("Days Remaining: ", remainder);

        // Now, we have to reduce the number of days by years in a year cycle
        for (let y = 0; y < yearsArray.length; y++) {
          //If the number of days is greater than the number of days for a cycle year
          if (bigInt(remainder).geq(yearsArray[y])) {
            this.log(
              `Removing ${yearsArray[y]} Days`
              // remainder,
              // yearsArray[y]
            );
            //Reduce the number of days by the cycle-year's number of days
            remainder = bigInt(remainder).minus(yearsArray[y]);
            //Add a year
            years += 1;
            this.log("Next year: ", years);
          } else break; //Stop the loop, we've gotten less days than a cycle year
        }

        //Set the number of years and days
        dateCumulative.year = years;
        dateCumulative.day = remainder.toJSNumber();

        //Skip processing the current iteration
        continue;
      }

      //We're factoring for something besides years, days, or seconds, so we set the unit value
      //on the returned object, and reduce the date string by the amount to finish the current iteration
      dateCumulative[unit] = unitCount;
      dateString = bigInt(dateString).minus(
        bigInt(unitDuration).times(unitCount)
      );
    }

    //Post-loops processing: Take any remaining number of seconds and assign it.
    dateCumulative.second = dateString.toJSNumber();

    //Next, we need to modify the values depending on if the date was negative
    const mult = isNegative ? -1 : 1;

    //Get all keys from the date object
    const cumulativeKeys = Object.keys(dateCumulative);
    this.log("DATE CUMULATIVE (TFO)", { ...dateCumulative });
    //Loop through all units in the date object and set them to positive or negative
    for (let k = 0; k < cumulativeKeys.length; k++) {
      const key = cumulativeKeys[k];
      const value = dateCumulative[key];
      dateCumulative[key] = bigInt(value).times(mult).toJSNumber();
    }

    //Prepare and return the final result
    const date = { ...dateCumulative };
    this.log("Absolute Date:", date);
    // console.groupEnd();
    return date;
  }

  //Transform time elapsed into current values
  toRelativeDate(dateObject) {
    // console.group('====BEGIN CONVERT TO RELATIVE====');

    //If there's no dateObject passed, grab the current stored
    //date
    if (!dateObject) {
      dateObject = this.toFullObject();
      this.log("Absolute Date to convert", dateObject);
    }
    this.log("Start From Absolute", { ...dateObject });
    //Spread the old object into a new copy
    dateObject = { ...dateObject };

    //Check if the date is negative
    let isNegative = false;
    const unitKeys = Object.keys(dateObject);
    for (let k = 0; k < unitKeys.length; k++) {
      //If any property's value is less than 0, then we know the result should be negative
      if (dateObject[unitKeys[k]] < 0) {
        isNegative = true;
        break;
      }
    }

    //Destructure the months from the current Calendar
    const { months } = this.calendar;

    //Get the months that have a valid leap configuration set
    const leapMonths = months.filter((m) => m.leapPeriod > 0 && m.leapDays > 0);

    //Determine how many years it takes for all leap months to have occurred at least once.
    const cycleLength = leapMonths.reduce((t, month) => {
      return t * month.leapPeriod;
    }, 1); //Gregorian: 4 years

    //Calculate the current month and day
    if (!isNegative) {
      this.log("Calculating positive relative date!");
      //Get the years that are left over after all leap cycle years
      const yearsRemaining = dateObject.year % cycleLength;
      //Since we're on a positive date, add a day to reflect the "current" day
      dateObject.day = dateObject.day + 1;
      //Set the starting month to the first one.
      dateObject.month = 0;

      //Loop through the months in order, subtracting days and incrementing the month index
      for (let m = 0; m < months.length; m++) {
        //Get the month at the current pointer index
        const month = months[m];
        //Determine if the year supplied is a leap year, according to the current selected month
        const isLeapYear = yearsRemaining % month.leapPeriod == 0;
        //Get the number of days to compare/remove, based on if the year is a leap year
        const daysToCompare = isLeapYear
          ? month.days + month.leapDays
          : month.days;

        //Compare the days'
        if (daysToCompare < dateObject.day) {
          //The number of days in the date is more than the current selected month,
          //so we'll remove the compared number of days and set the month index
          dateObject.day -= daysToCompare;
          dateObject.month = m + 1;

          //Sanity Check: If the new pointer is out of bounds, we need to reset it,
          //and increment the year, then go back through the loop
          if (dateObject.month >= months.length) {
            // we're larger than the total amount of months, so remove max and add +1 to year
            dateObject.month = dateObject.month % months.length;
            dateObject.year += 1;
          }
        } else {
          //The number of days in the date is less than or equal to the number of days
          //in the current selected month, so we can stop looping!
          break;
        }
      }
    } else {
      this.log("Calculating negative relative date!");
      // dateObject.day = dateObject.day + 1;
      //When calculating negative, the 'day' property indicates how many days to remove from the year (365/366 for leaps)
      //So, if we simply determine the days in that year, and subtract the 'day' (absolute) from that, we'll get the day of the year
      //Then, we can simply go forward through the months, reducing the 'day' by the number of days in each month until we get the result
      //(basically, fix the day and then use the logic for positive dates to determine day/month)
      let year = dateObject.year;
      year--;
      this.log("Year (as relative, so we increased absolute value by 1)", year);
      const cycle = this.getCycle().reverse();
      let remainingYears = Math.abs(year) % cycle.length;
      this.log("Remaining years:", remainingYears);
      const daysInYear =
        remainingYears <= 0
          ? cycle[cycle.length + (remainingYears - 1)]
          : cycle[remainingYears - 1];
      this.log("Days in current year", daysInYear);
      let day = daysInYear - Math.abs(dateObject.day);
      day++;
      this.log("Current day of year", day);
      let currentMonth = 0;
      for (let m = 0; m < months.length; m++) {
        const month = months[m];

        const isLeapYear = (year - 0) % month.leapPeriod === 0;
        this.log("is leap year for month", m, isLeapYear);
        const daysToCompare = isLeapYear
          ? month.days + month.leapDays
          : month.days;

        if (daysToCompare < day) {
          this.log("Adjusting Day. Removing days ", daysToCompare);
          day -= daysToCompare;
          currentMonth = m + 1;

          if (currentMonth >= months.length) {
            this.log(
              "This shouldnt be ran!!! We are removing years (adding negative years!!)"
            );
            currentMonth = currentMonth % months.length;
            year++;
          }
        } else {
          this.log(
            "Year",
            year,
            "Day of Month",
            day,
            "Month",
            months[currentMonth]
          );
          break;
        }
      }

      if (day === 0) day = 1;

      dateObject = {
        ...dateObject,
        year,
        month: currentMonth,
        day,
      };
    }

    this.log("Relative Date Result", dateObject);
    // console.groupEnd();
    return dateObject;
  }

  format(dateMask, dateObject, posSuffix, negSuffix) {
    const isNegative = this.isNegative(dateObject);

    const tokens = Object.keys(DateTimeTokens);
    const date =
      !dateObject || typeof dateObject === "string"
        ? this.toFullObject(dateObject)
        : dateObject;

    // console.group('=== Formatter');
    this.log("Formatting Date", date);
    let relativeDate = null;
    if (date.month !== undefined) {
      relativeDate = date;
    } else {
      relativeDate = this.toRelativeDate(date);
    }
    this.log("Relative Date", relativeDate);

    for (let t = 0; t < tokens.length; t++) {
      let token = tokens[t];
      token = token.replace(/\$/g, "\\$");
      const tokenDescription = DateTimeTokens[tokens[t]];

      const regex = new RegExp(token, "g");
      const matches = [...dateMask.matchAll(regex)];
      if (matches && matches.length > 0) {
        let tokenGranularity = "";
        switch (tokenDescription) {
          case "day":
          case "dayPadded": {
            tokenGranularity = "day";
            dateMask = dateMask.replace(
              regex,
              parseInt(relativeDate["day"], 10)
            );
            break;
          }

          case "monthNum":
          case "monthNumPadded": {
            tokenGranularity = "month";
            dateMask = dateMask.replace(
              regex,
              parseInt(relativeDate["month"], 10)
            );
            break;
          }

          case "monthAbbr": {
            tokenGranularity = "month";
            const monthIndex = parseInt(relativeDate["month"], 10);
            dateMask = dateMask.replace(
              regex,
              this.calendar.months[monthIndex].name.subStr(0, 3)
            );
            break;
          }

          case "monthFull": {
            tokenGranularity = "month";
            const monthIndex = parseInt(relativeDate["month"], 10);
            // console.log('Months', this.calendar.months);
            // console.log('Month Index', monthIndex, date);
            dateMask = dateMask.replace(
              regex,
              this.calendar.months[monthIndex].name
            );
            break;
          }

          case "yearAbbr":
          case "yearNum": {
            tokenGranularity = "year";
            const year = relativeDate["year"];
            let yearStr = year.toString();
            this.log("Year STR in Format:", yearStr, relativeDate);
            if (posSuffix && !isNegative) {
              yearStr += ` ${posSuffix}`;
              // console.warn('Positive!', yearStr);
            }
            if (negSuffix && isNegative) {
              yearStr = yearStr.replace(/-/g, "");
              yearStr += ` ${negSuffix}`;
            }

            dateMask = dateMask.replace(regex, yearStr);
            break;
          }
        }
      }
    }
    this.log("Formatted Date: ", dateMask);
    // console.groupEnd();

    //Return Final Result
    return dateMask;
  }

  //Simply set the current date equal to a given string.
  setDateFromString(dateInput) {
    const dateString = dateInput.slice();
    this.input = dateString;
    return dateString;
  }

  convertRelativeToAbsolute(relativeDate) {
    /**
     * Convert a Relative date to absolute object
     *
     *  Relative to Absolute in + space
     *    year = Ryear
     *    day = Month(m)*Month(m).length + Rday
     *    hour = Rhour
     *    minute = Rminute
     *  Relative to Absolute in - space
     *    year = Ryear +1 (because - space is 1 based)
     *    day = Ryear.length.days -(Month(m)*Month(m).length + Rday) (because we calculate for endOfYear in the absolute date!)
     *    hour = Rhour
     *    minute = Rminute
     *
     */
    // console.group('=== Converting To Absolute');
    if (relativeDate.month === undefined) {
      return relativeDate;
    }
    this.log("Relative Date:", { ...relativeDate });
    const absoluteDate = {};
    const { time, months } = this.calendar;
    let isNegative = false;
    let unitKeys = Object.keys(relativeDate);
    for (let k = 0; k < unitKeys.length; k++) {
      if (relativeDate[unitKeys[k]] < 0) {
        isNegative = true;
        break;
      }
    }

    if (!isNegative) {
      let year = relativeDate.year;
      let dayOfYear = 0;
      let hour = relativeDate.hour;
      let minute = relativeDate.minute;
      let second = relativeDate.second !== undefined ? relativeDate.second : 0;

      for (let m = 0; m < months.length; m++) {
        const month = months[m];
        if (relativeDate.month > m) {
          let daysToAdd = month.days;
          //Check if leap year
          if (month.leapPeriod > 0 && month.leapDays > 0) {
            if (year % month.leapPeriod === 0) {
              daysToAdd += month.leapDays;
            }
          }
          //Add days to dayOfYear
          dayOfYear += daysToAdd;
        } else {
          //Add remaining days in relativeDate -1
          dayOfYear += relativeDate.day - 1;
          //set absoluteDate.day to total of dayOfYear
          break;
        }
      }

      absoluteDate.year = year;
      absoluteDate.day = dayOfYear;
      absoluteDate.hour = hour;
      absoluteDate.minute = minute;
      absoluteDate.second = second;
    } else {
      this.log("Transforming negative RelDate to Absolute");
      let year = Math.abs(relativeDate.year) * -1 + 1;
      let dayOfYear = Math.abs(relativeDate.day) - 0;
      let hour = Math.abs(relativeDate.hour) * -1;
      let minute = Math.abs(relativeDate.minute) * -1;
      let second =
        relativeDate.second !== undefined
          ? Math.abs(relativeDate.second) * -1
          : 0;

      for (let m = 0; m < months.length; m++) {
        const month = months[m];
        if (m < relativeDate.month) {
          let daysToAdd = month.days;
          //Check for leap
          if (month.leapPeriod > 0 && month.leapDays > 0) {
            if ((Math.abs(year) + 1) % month.leapPeriod === 0) {
              daysToAdd += month.leapDays;
            }
          }
          dayOfYear += daysToAdd;
          //Add days to dayOfYear
          this.log(`Adding days for ${month.name}: ${daysToAdd}`);
        } else {
          //Add remaining days in relativeDate
          // dayOfYear += Math.abs(relativeDate.day) - 1;
          // console.log(
          //   `RelMonth is ${month.name}, adding ${relativeDate.day} days and ending`
          // );
          break;
        }
      }

      //Find length of year
      const cycle = this.getCycle(); //Don't reverse, as we're calculating as if we're positive
      this.log("CYCLE", cycle);
      this.log("YEAR", year);
      this.log("IS LEAP YEAR: ", (Math.abs(year) + 1) % cycle.length === 0);
      const remainingYears = (Math.abs(year) + 1) % cycle.length;
      const nonLeapYearLength = bigInt(time.year.duration)
        .divide(time.day.duration)
        .toJSNumber();
      const yearLength = cycle[remainingYears];
      // this.log('Day of year (before reversing): ', dayOfYear);
      dayOfYear = yearLength - (dayOfYear - 1);
      this.log("Day of year (after reversing): ", dayOfYear, yearLength);
      absoluteDate.year = year;
      absoluteDate.day = Math.abs(dayOfYear) * -1 - 0;
      absoluteDate.hour = hour;
      absoluteDate.minute = minute;
      absoluteDate.second = second;
    }

    this.log("ConvertToAbsolute Result:", { ...absoluteDate });
    // console.groupEnd();
    return absoluteDate;
  }

  setDateFromObject(dateInput) {
    // console.group('Set Date From Object', { ...dateInput });
    if (!dateInput) {
      dateInput = this.toFullObject(this.input);
    }

    if (dateInput.month !== undefined) {
      this.log("Need to convert RelativeDate to AbsoluteDate");
      this.log("Relative Date", dateInput);
      dateInput = this.convertRelativeToAbsolute(dateInput);
      this.log("AbsoluteDate Date", dateInput);
    }
    this.log("Start from object", dateInput);
    //First, clone the input so we don't trigger anything in case of modification
    const dateObject = { ...dateInput };

    let isNegative = this.isNegative(dateObject);

    // const dateKeys = Object.keys(dateObject);
    // for (let u = 0; u < dateKeys.length; u++) {
    //   if (dateObject[dateKeys[u]] < 0) {
    //     isNegative = true;
    //     break;
    //   }
    // }

    const { time, months } = this.calendar;

    const leapMonths = months.filter((m) => m.leapPeriod > 0 && m.leapDays > 0);
    const totalLeapFreq = leapMonths.reduce((t, month) => {
      return t * month.leapPeriod;
    }, 1);

    // this.log('Total Freq', totalLeapFreq);
    let yearsArray = Array(totalLeapFreq).fill(0);

    for (let y = 0; y < yearsArray.length; y++) {
      let daysInThatYear = bigInt(time.year.duration)
        .divide(time.day.duration)
        .toJSNumber();
      for (let m = 0; m < months.length; m++) {
        if (y % months[m].leapPeriod === 0) {
          daysInThatYear += months[m].leapDays;
        }
      }
      yearsArray[y] = daysInThatYear;
    }
    // this.log('Years Array', yearsArray);
    if (isNegative) {
      yearsArray.reverse();
    }
    this.log("New Years Array", yearsArray);
    const cycles = bigInt(dateInput.year).divide(yearsArray.length);
    this.log("Number of Leap Cycles", cycles);
    let remainingYears = bigInt(dateInput.year)
      .abs()
      .mod(yearsArray.length)
      .toJSNumber();
    this.log("Number of remaining years", remainingYears);
    this.log(
      "Total years",
      Math.abs(cycles * yearsArray.length) + remainingYears
    );
    const sumOfAllLeapDays = yearsArray.reduce((t, year) => {
      return t + year;
    }, 0);
    let days = bigInt(sumOfAllLeapDays).times(Math.abs(cycles));
    for (let y = 0; y < yearsArray.length; y++) {
      if (remainingYears >= 1) {
        remainingYears -= 1;
        days = bigInt(days).plus(yearsArray[y]);
        this.log(`Adding ${yearsArray[y]} days`);
      } else break;
    }
    days = bigInt(days).plus(Math.abs(dateInput.day));
    this.log("Days", days);
    let date = bigInt("0")
      .plus(bigInt(days).times(time.day.duration))
      .plus(bigInt(dateInput.hour).times(time.hour.duration))
      .plus(bigInt(dateInput.minute).times(time.minute.duration))
      .plus(dateInput.second ? dateInput.second : 0);

    if (isNegative) {
      date = date.times(-1);
    }

    this.log("RETURN DATE", date.toString());
    // this.log('Date As Obj', this.toFullObject(date));

    // console.groupEnd();
    return date.toString();
  }

  getUnitsOf(desiredUnit, fromUnit, fromUnitQty = 1) {
    const unit = desiredUnit;
    if (!this.calendar.time[unit] && desiredUnit !== "second") {
      console.error(`Unknown desired unit "${unit}".`);
      return;
    }
    const unitDuration =
      desiredUnit === "second" ? "1" : this.calendar.time[unit].duration;
    const fromUnitDuration = this.calendar.time[fromUnit].duration;
    const unitCount = bigInt(fromUnitDuration)
      .times(fromUnitQty)
      .divide(unitDuration)
      .toJSNumber();
    return unitCount;
  }

  addToRelativeDate(relativeDate, granularity, amount) {
    const { time, months } = this.calendar;
    if (granularity === "day") {
      let currentMonth = relativeDate.month;
      let currentDayOfMonth = relativeDate.day;
      let currentYear = relativeDate.year;
      let monthObj = months[currentMonth];
      const isLeapMonth =
        monthObj.leapPeriod > 0 &&
        monthObj.leapDays > 0 &&
        currentYear % monthObj.leapPeriod === 0;
      let daysInMonth = isLeapMonth
        ? monthObj.days + monthObj.leapDays
        : monthObj.days;
      if (currentDayOfMonth + amount <= daysInMonth) {
        currentDayOfMonth += amount;
      } else if (currentDayOfMonth + amount > daysInMonth) {
        let difference = currentDayOfMonth + amount - daysInMonth;
        currentDayOfMonth = difference;
        currentMonth++;
      }

      if (currentMonth >= months.length) {
        let difference = currentMonth - months.length;
        currentYear++;
        currentMonth = difference;
      }

      relativeDate.month = currentMonth;
      relativeDate.day = currentDayOfMonth;
      relativeDate.year = currentYear;
    }

    if (granularity === "hour") {
      let { year, month, day, hour } = relativeDate;
      const hoursInDay = bigInt(time.day.duration)
        .divide(time.hour.duration)
        .toJSNumber();
      if (hour + amount < hoursInDay) {
        hour += amount;
      } else {
        const diff = hour + amount - hoursInDay;
        hour = diff;
        const monthObj = months[month];
        const isLeapMonth =
          monthObj.leapPeriod > 0 &&
          monthObj.leapDays > 0 &&
          year % monthObj.leapPeriod === 0;
        let daysInMonth = isLeapMonth
          ? monthObj.days + monthObj.leapDays
          : monthObj.days;

        if (day + 1 <= daysInMonth) {
          day++;
        } else {
          const diffDays = daysInMonth - (day + 1);
          day = diffDays;
          month++;
        }
      }
      if (month >= months.length) {
        const diffMonths = month - months.length;
        month = diffMonths;
        year++;
      }

      relativeDate.year = year;
      relativeDate.month = month;
      relativeDate.day = day;
      relativeDate.hour = hour;
    }

    return { ...relativeDate };
  }

  add(amount, granularity, offset = "0") {
    const { time } = this.calendar;
    const unitDuration = time[granularity].duration;
    if (time[granularity]) {
      const isNegative = this.isNegative();
      const mult = isNegative ? -1 : 1;
      const advancedUnits = [
        "aeon",
        "epoch",
        "myriad",
        "millenium",
        "century",
        "decade",
        "year",
      ];
      if (advancedUnits.indexOf(granularity) > -1) {
        const yearsInUnit = bigInt(unitDuration).divide(time.year.duration);

        //TEST//
        const absoluteDate = this.toFullObject();
        const yearsToAdd = bigInt(amount).times(yearsInUnit).toJSNumber();
        this.log("Years to Add", yearsToAdd);
        this.log("Before Adding", absoluteDate.year);
        const updatedDate = {
          ...absoluteDate,
          year: absoluteDate.year + yearsToAdd,
        };
        if (updatedDate.year >= 0) {
          const keys = Object.keys(updatedDate);
          for (let i = 0; i < keys.length; i++) {
            if (bigInt(updatedDate[keys[i]]).lt(0)) {
              updatedDate[keys[i]] = bigInt(updatedDate[keys[i]])
                .abs()
                .toJSNumber();
            }
          }
        } else {
          const keys = Object.keys(updatedDate);
          for (let i = 0; i < keys.length; i++) {
            if (bigInt(updatedDate[keys[i]]).geq(0)) {
              updatedDate[keys[i]] = bigInt(updatedDate[keys[i]])
                .times("-1")
                .toJSNumber();
            }
          }
        }
        this.log("New Date Object", updatedDate);
        const result = this.setDateFromObject(updatedDate);
        offset = bigInt(result).lt(0)
          ? bigInt(offset)
          : bigInt(offset).times("-1");
        this.log("Offset: ", offset.toString());
        const resultDate = bigInt(result).lt(0)
          ? bigInt(result).add(offset).toString()
          : bigInt(result).subtract(offset).toString();
        this.log("Result Date", resultDate);
        this.input = resultDate;
        return this.input;
        //END TEST//
      }
      const updatedDate = bigInt(this.input).plus(
        bigInt(amount).times(unitDuration)
      );
      offset = bigInt(updatedDate).lt(0)
        ? bigInt(offset)
        : bigInt(offset).times("-1");
      this.log("Offset: ", offset.toString());
      const newValue = bigInt(updatedDate).lt(0)
        ? bigInt(updatedDate).add(offset).toString()
        : bigInt(updatedDate).minus(offset).toString();
      this.log("Result Date", newValue);
      this.input = newValue;
      return this.input;
    }
  }

  subtract(amount, granularity, offset = "0") {
    const { time } = this.calendar;
    if (time[granularity]) {
      const unitDuration = time[granularity].duration;
      const isNegative = this.isNegative();
      const mult = isNegative ? -1 : 1;
      const advancedUnits = [
        "aeon",
        "epoch",
        "myriad",
        "millenium",
        "century",
        "decade",
        "year",
      ];
      if (advancedUnits.indexOf(granularity) > -1) {
        const yearsInUnit = bigInt(unitDuration).divide(time.year.duration);

        //TEST//
        const absoluteDate = this.toFullObject();
        const yearsToRemove = bigInt(amount).times(yearsInUnit).toJSNumber();
        this.log("Years to Remove", yearsToRemove);
        this.log("Before Removing", absoluteDate.year);
        const updatedDate = {
          ...absoluteDate,
          year: absoluteDate.year - yearsToRemove,
        };
        this.log("Updated Date:", updatedDate);
        if (updatedDate.year >= 0) {
          const keys = Object.keys(updatedDate);
          for (let i = 0; i < keys.length; i++) {
            if (bigInt(updatedDate[keys[i]]).lt(0)) {
              updatedDate[keys[i]] = bigInt(updatedDate[keys[i]])
                .abs()
                .toJSNumber();
            }
          }
        } else {
          const keys = Object.keys(updatedDate);
          for (let i = 0; i < keys.length; i++) {
            if (bigInt(updatedDate[keys[i]]).geq(0)) {
              updatedDate[keys[i]] = bigInt(updatedDate[keys[i]])
                .times("-1")
                .toJSNumber();
            }
          }
        }
        this.log("New Date Object", updatedDate);
        const result = this.setDateFromObject(updatedDate);
        offset = bigInt(result).lt(0)
          ? bigInt(offset).times("-1")
          : bigInt(offset);
        this.log("Offset: ", offset.toString());
        const resultDate = bigInt(result).lt(0)
          ? bigInt(result).subtract(offset).toString()
          : bigInt(result).add(offset).toString();
        this.input = resultDate;
        this.log("Result Date", this.input, resultDate);
        return this.input;
        //END TEST//
      }
      const updatedDate = bigInt(this.input).minus(
        bigInt(amount).times(unitDuration)
      );
      offset = bigInt(updatedDate).lt(0)
        ? bigInt(offset).times("-1")
        : bigInt(offset);
      this.log("Offset ", offset);
      const newValue = bigInt(updatedDate).lt(0)
        ? bigInt(updatedDate).subtract(offset).toString()
        : bigInt(updatedDate).add(offset).toString();
      this.log("Result Date", newValue);
      this.input = newValue;
      return this.input;
    }
  }

  jumpTo(value, granularity, displayUnits = 1000) {
    const { time } = this.calendar;
    if (time[granularity]) {
      const advancedUnits = [
        "aeon",
        "epoch",
        "myriad",
        "millenium",
        "century",
        "decade",
        "year",
      ];

      if (advancedUnits.indexOf(granularity) > -1) {
        //Advanced calc
        const yearsInUnit = bigInt(time[granularity].duration).divide(
          time.year.duration
        );
        // console.log("Years in Unit", yearsInUnit);
        const isNegative = bigInt(value).lt(0);
        let year = bigInt(value).abs().times(yearsInUnit);
        console.log("Desired Year: (before leap calc)", year);
        const cycle = this.getCycle();
        if (isNegative) {
          cycle.reverse();
        }
        const cycleCount = bigInt(year).divide(cycle.length);
        this.log("Number of Full leap cycles", cycleCount);
        const daysInCycle = cycle.reduce((t, cycleYear) => {
          return t + cycleYear;
        }, 0);
        let days = cycleCount.times(daysInCycle);

        this.log("Initial Days", days.toJSNumber());
        let remainingYears = bigInt(year).mod(cycle.length);
        this.log("Remaining Years", remainingYears.toJSNumber());
        for (let y = 0; y < cycle.length; y++) {
          if (remainingYears.eq(0)) break;
          remainingYears = remainingYears.minus(1);
          days = days.plus(cycle[y]);
        }
        this.log("Days Total", days.toJSNumber());
        const daysInSeconds = bigInt(time.day.duration)
          .times(days)
          .times(isNegative ? "-1" : "1");
        // .times(yearsInUnit);
        return daysInSeconds.toString();
      } else {
        //Basic Calc
        const unitDuration = time[granularity].duration;
        const valueDuration = bigInt(value).times(unitDuration).toString();
        this.log(this.abs(), this.abs().length);
        this.log(valueDuration, valueDuration.length);
        this.log(this.abs().substr(0, -valueDuration.length - 1));
        return valueDuration;
      }
    }
  }

  getDateFromPosition(position, granularity, startDate, unitCount = 1000) {
    const { time } = this.calendar;
    if (startDate === undefined) {
      startDate = this.getTimelineSegment(granularity, {
        unitCountDisplay: unitCount,
      }).start;
    }
    this.log("Position received", position);
    this.log("Granularity received", granularity);
    const unitsFromStart = Math.floor(position / 15);
    const advancedUnits = [
      "aeon",
      "epoch",
      "myriad",
      "millenium",
      "century",
      "decade",
      "year",
    ];
    if (advancedUnits.indexOf(granularity) < 0) {
      this.log("Calculating on basic math");
      //Simple Calc
      const unitDuration = time[granularity].duration;
      const timeToAdd = bigInt(unitDuration).times(unitsFromStart);
      return bigInt(startDate).plus(timeToAdd).toString();
    } else {
      this.log("Calculating on Advanced Math");
      //Advanced Calc

      //First, get the number of years in each granularity unit (year: 1, decade: 10, etc)
      const yearsInUnit = bigInt(time[granularity].duration).divide(
        time.year.duration
      );

      //Next, we need to factor for leap years
      let years = bigInt(yearsInUnit).times(unitsFromStart);
      this.log("Number of years from start:", years.toJSNumber());

      //Get years in days (including leapDays)
      //- get startDate as absObj
      let dateObject = this.toFullObject(startDate);
      this.log("Starting Date", { ...dateObject });
      //- add years to absObj.years
      dateObject.year += years.toJSNumber();
      //- convert absObj to string
      this.log("Starting Date (after years add)", { ...dateObject });
      const newDate = this.setDateFromObject({ ...dateObject });
      return newDate;
    }
  }
}
